import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { InputObject } from "@canvas-sync/shared";
import { api, uploadToS3 } from "../api/client";
import { useCanvasEnv } from "../lib/envContext";
import {
  validateEnrollmentsCsv,
  type CsvValidationResult,
  type CsvIssue,
} from "../lib/csvValidate";
import { formatBytes, formatDateTime, formatRelative } from "../lib/format";

type UploadState = "idle" | "validating" | "uploading" | "starting";

const MAX_BYTES = 25 * 1024 * 1024;

/**
 * New-sync page.
 *
 * Three things happen here:
 *  1. Pick or drag-drop a CSV → we read it locally, validate against the
 *     PeopleSoft enrollments schema, and show errors/warnings inline.
 *  2. The "Start sync" button uploads to S3 (presigned POST) and creates
 *     a run. The per-run auto-submit toggle defaults to the global setting
 *     and is a one-shot override.
 *  3. The "Previously uploaded" list shows what's actually in
 *     s3://<app-bucket>/inputs/<env>/ so the user can restart a sync from
 *     an existing file without re-uploading.
 */
export function InputsPage() {
  const { env } = useCanvasEnv();
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<CsvValidationResult | null>(
    null,
  );
  const [state, setState] = useState<UploadState>("idle");
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [globalDefault, setGlobalDefault] = useState<boolean>(true);
  const [autoSubmit, setAutoSubmit] = useState<boolean>(true);
  const nav = useNavigate();

  // Previously uploaded files in S3 (reloads when the env switcher flips).
  const [inputs, setInputs] = useState<InputObject[] | null>(null);
  const [inputsErr, setInputsErr] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  // Load the global default-auto-submit from settings on mount + on env flip.
  useEffect(() => {
    let cancelled = false;
    api
      .getSettings()
      .then((s) => {
        if (cancelled) return;
        const def = s.defaultSubmitToCanvas !== false;
        setGlobalDefault(def);
        setAutoSubmit(def);
      })
      .catch(() => {
        /* keep the current value — operator can still tick/untick manually */
      });
    return () => {
      cancelled = true;
    };
  }, [env]);

  // Load the inputs list. Refetch when env flips OR when a new upload finishes
  // (refreshTick), so the user sees their file land in the list immediately.
  useEffect(() => {
    let cancelled = false;
    setInputs(null);
    setInputsErr(null);
    // Fetch up to 200 — the backend caps higher; the card itself paginates
    // visually so we always have enough headroom to surface week-old files
    // when the user searches for them.
    api
      .listInputs(200)
      .then((res) => {
        if (!cancelled) setInputs(res.inputs);
      })
      .catch((e) => {
        if (!cancelled) setInputsErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, [env, refreshTick]);

  async function handleFile(f: File) {
    setError(null);
    if (f.size > MAX_BYTES) {
      setError("File exceeds the 25 MB limit.");
      setFile(null);
      setValidation(null);
      return;
    }
    setFile(f);
    setState("validating");
    try {
      const text = await f.text();
      setValidation(validateEnrollmentsCsv(text));
    } catch (e) {
      setError(`Couldn't read the file: ${(e as Error).message}`);
      setValidation(null);
    } finally {
      setState("idle");
    }
  }

  function clearFile() {
    setFile(null);
    setValidation(null);
    setError(null);
  }

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    // Reset the input so picking the same file twice re-triggers onChange.
    e.target.value = "";
  }

  function onDragEnter(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (state === "idle" || state === "validating") setDragOver(true);
  }
  function onDragOver(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
  }
  function onDragLeave(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  }
  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    if (state !== "idle") return;
    const f = e.dataTransfer?.files?.[0];
    if (!f) return;
    if (!/\.csv$/i.test(f.name) && f.type !== "text/csv") {
      setError(`"${f.name}" isn't a CSV file.`);
      return;
    }
    handleFile(f);
  }

  function toggleAutoSubmit(next: boolean) {
    setAutoSubmit(next);
  }

  async function handleRun() {
    if (!file) return;
    if (validation && !validation.valid) {
      setError("Fix the validation errors before starting the sync.");
      return;
    }
    setError(null);

    try {
      setState("uploading");
      const presigned = await api.getInputUploadUrl(file.name);
      const s3Uri = await uploadToS3(file, presigned);

      setState("starting");
      const run = await api.createRun({
        trigger: "manual",
        triggeredBy: "dashboard",
        inputS3Uri: s3Uri,
        submitToCanvas: autoSubmit,
      });

      nav(`/syncs/${run.runId}`);
    } catch (e) {
      setError((e as Error).message);
      setState("idle");
    }
  }

  async function handleStartFromExisting(input: InputObject) {
    setError(null);
    try {
      setState("starting");
      const run = await api.createRun({
        trigger: "manual",
        triggeredBy: "dashboard",
        inputS3Uri: input.s3Uri,
        submitToCanvas: autoSubmit,
      });
      nav(`/syncs/${run.runId}`);
    } catch (e) {
      setError((e as Error).message);
      setState("idle");
    }
  }

  const busy = state === "uploading" || state === "starting";
  const isProd = env === "prod";
  const startDisabled =
    !file ||
    busy ||
    state === "validating" ||
    (validation !== null && !validation.valid);

  return (
    <section>
      <div className="page-head">
        <h1>Start a new sync</h1>
        <div className="muted">
          Upload a PeopleSoft enrollment CSV to begin.
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Upload a CSV</h2>
        </div>

        <div className="upload-zone">
          <label
            htmlFor="file-input"
            className={
              "file-drop" +
              (file ? " file-drop-has" : "") +
              (dragOver ? " file-drop-dragging" : "")
            }
            onDragEnter={onDragEnter}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
          >
            <input
              id="file-input"
              type="file"
              accept=".csv,text/csv"
              disabled={busy}
              onChange={onPick}
            />
            {file ? (
              <>
                <div className="file-name">{file.name}</div>
                <div className="muted small">
                  {(file.size / 1024).toFixed(1)} KB
                  {validation && (
                    <>
                      {" "}
                      · {validation.dataRows} data row
                      {validation.dataRows === 1 ? "" : "s"}
                    </>
                  )}
                </div>
                <div className="muted small" style={{ marginTop: 6 }}>
                  Drop a different file, or{" "}
                  <button
                    type="button"
                    className="muted-link"
                    style={{
                      background: "none",
                      border: 0,
                      padding: 0,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      clearFile();
                    }}
                  >
                    clear
                  </button>
                  .
                </div>
              </>
            ) : dragOver ? (
              <>
                <div className="file-prompt">Drop to upload</div>
                <div className="muted small">CSV only, up to 25 MB.</div>
              </>
            ) : (
              <>
                <div className="file-prompt">Choose or drop a CSV here</div>
                <div className="muted small">CSV only, up to 25 MB.</div>
              </>
            )}
          </label>

          {state === "validating" && (
            <div className="muted small">Checking the file…</div>
          )}

          {validation && <ValidationPanel result={validation} />}

          <div className={"auto-submit-toggle" + (autoSubmit ? " on" : "")}>
            <label className="toggle">
              <input
                type="checkbox"
                disabled={busy}
                checked={autoSubmit}
                onChange={(e) => toggleAutoSubmit(e.target.checked)}
              />
              <span className="toggle-track" />
              <span className="toggle-label">
                Automatically send the fix CSV to Canvas (
                {isProd ? "production" : "test"}) when this sync finishes
              </span>
            </label>
            <div className="muted small auto-submit-detail">
              {autoSubmit ? (
                isProd ? (
                  <>
                    The fix CSV will be sent to{" "}
                    <strong>production Canvas</strong> automatically — this
                    can't be undone. Turn this off for this sync if you want to
                    review before sending.
                  </>
                ) : (
                  <>
                    When this sync finishes, the fix CSV is sent to Canvas
                    (test) automatically. You can find it in{" "}
                    <a href="/uploads" className="muted-link">
                      Upload history
                    </a>
                    , linked back to this sync.
                  </>
                )
              ) : (
                <>
                  Off for this sync. The sync will still produce a fix CSV, but
                  nothing is sent to Canvas until you send it yourself.
                </>
              )}
              {autoSubmit !== globalDefault && (
                <>
                  {" "}
                  <em>
                    (This overrides your default of{" "}
                    <strong>{globalDefault ? "ON" : "OFF"}</strong>; change the
                    default in{" "}
                    <a href="/settings" className="muted-link">
                      Settings
                    </a>
                    .)
                  </em>
                </>
              )}
            </div>
          </div>

          <button
            className="btn btn-primary btn-large"
            disabled={startDisabled}
            onClick={handleRun}
          >
            {state === "idle" &&
              (autoSubmit
                ? isProd
                  ? "Start sync + auto-submit to production"
                  : "Start sync + auto-submit to test"
                : "Start sync")}
            {state === "validating" && "Checking file…"}
            {state === "uploading" && "Uploading file…"}
            {state === "starting" && "Starting sync…"}
          </button>

          {error && <div className="error">{error}</div>}
        </div>
      </div>

      <PreviousInputsCard
        inputs={inputs}
        loadingErr={inputsErr}
        busy={busy}
        env={env}
        onRefresh={() => setRefreshTick((n) => n + 1)}
        onStartFromExisting={handleStartFromExisting}
      />

      <div className="card">
        <div className="card-head">
          <h2>What happens next</h2>
        </div>
        <ol className="flow-list">
          <li>
            Your file uploads to secure storage directly from your browser.
          </li>
          <li>We start a new sync and you're taken to its detail page.</li>
          <li>
            You'll see live progress as the sync checks your enrollment data
            against Canvas.
          </li>
          <li>
            When the sync finishes (about 5–10 minutes), a fix CSV is ready to
            download.
          </li>
          {autoSubmit && (
            <li>
              <strong>Auto-submit is on:</strong> if any ghost enrollments were
              found, the fix CSV is sent to Canvas automatically as a new upload
              — linked back to this sync. You can track it on the upload detail
              page.
            </li>
          )}
        </ol>
        <details className="raw-details">
          <summary>Show technical details</summary>
          <ol className="flow-list" style={{ marginTop: 8 }}>
            <li>
              The file is uploaded directly to S3 using a presigned POST (the
              browser never proxies through the API).
            </li>
            <li>
              The API creates a run record in DynamoDB and starts the Step
              Functions state machine.
            </li>
            <li>
              Step Functions runs the ECS Fargate worker with{" "}
              <code>RUN_ID</code> + <code>INPUT_KEY</code> env vars.
            </li>
            <li>
              The worker streams structured events to CloudWatch — they appear
              in the activity log as they arrive.
            </li>
            <li>
              When the run finishes, the fix CSV is at{" "}
              <code>outputs/&lt;runId&gt;.csv</code> in S3.
            </li>
            {autoSubmit && (
              <li>
                If <code>submitToCanvas</code> is true, the AutoSubmit Lambda
                POSTs the fix CSV to Canvas's SIS Imports endpoint as a new
                upload with <code>trigger=auto</code>.
              </li>
            )}
          </ol>
        </details>
      </div>
    </section>
  );
}

function ValidationPanel({ result }: { result: CsvValidationResult }) {
  const { valid, errors, warnings, dataRows, headerOk } = result;
  const tone = !valid ? "error" : warnings.length > 0 ? "warn" : "valid";
  const headline = !valid
    ? `File has ${errors.length} problem${errors.length === 1 ? "" : "s"} — fix before continuing.`
    : warnings.length > 0
      ? `Looks OK — but ${warnings.length} row${warnings.length === 1 ? "" : "s"} flagged for review.`
      : `Looks good. ${dataRows} data row${dataRows === 1 ? "" : "s"} ready to sync.`;

  return (
    <div className={`validation-summary ${tone}`}>
      <div className="validation-summary-head">{headline}</div>
      {!headerOk && (
        <div style={{ marginTop: 6 }} className="muted small">
          The expected header is{" "}
          <code>"course_id","user_id","role","section_id","status"</code>.
        </div>
      )}
      {errors.length > 0 && (
        <IssueList title="Errors" items={errors} tone="errors" />
      )}
      {warnings.length > 0 && (
        <IssueList title="Warnings" items={warnings} tone="warnings" />
      )}
    </div>
  );
}

function IssueList({
  title,
  items,
  tone,
}: {
  title: string;
  items: CsvIssue[];
  tone: "errors" | "warnings";
}) {
  return (
    <div style={{ marginTop: 8 }}>
      <div className="small" style={{ marginBottom: 4, fontWeight: 600 }}>
        {title}
      </div>
      <ul className={`validation-issues ${tone}`}>
        {items.map((iss, i) => (
          <li key={i}>{iss.message}</li>
        ))}
      </ul>
    </div>
  );
}

const DEFAULT_VISIBLE = 5;

function PreviousInputsCard({
  inputs,
  loadingErr,
  busy,
  env,
  onRefresh,
  onStartFromExisting,
}: {
  inputs: InputObject[] | null;
  loadingErr: string | null;
  busy: boolean;
  env: "test" | "prod";
  onRefresh: () => void;
  onStartFromExisting: (input: InputObject) => void;
}) {
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  // Filter by filename. Trimmed case-insensitive substring match — same
  // behaviour as the search box on the Syncs / Uploads pages.
  const filtered = useMemo(() => {
    if (!inputs) return [];
    const q = search.trim().toLowerCase();
    if (!q) return inputs;
    return inputs.filter((i) => i.fileName.toLowerCase().includes(q));
  }, [inputs, search]);

  // Count uploads-per-content (eTag) across the current view so duplicate
  // hourly n8n uploads of the same file collapse visually into "×N" rather
  // than each taking a full row. n8n's state-file dedup means after the
  // next deploy this list will normally be one row per unique CSV content.
  const etagCounts = useMemo(() => {
    const m = new Map<string, number>();
    for (const item of filtered) {
      if (item.eTag) m.set(item.eTag, (m.get(item.eTag) ?? 0) + 1);
    }
    return m;
  }, [filtered]);

  const visible = showAll ? filtered : filtered.slice(0, DEFAULT_VISIBLE);
  const hidden = Math.max(0, filtered.length - visible.length);

  return (
    <div className="card">
      <div className="card-head">
        <h2>Previously uploaded</h2>
        <div className="muted small">
          {inputs
            ? `${inputs.length} file${inputs.length === 1 ? "" : "s"} in Canvas ${env}`
            : ""}
          <button
            type="button"
            className="muted-link"
            style={{
              background: "none",
              border: 0,
              padding: 0,
              marginLeft: 12,
              cursor: "pointer",
              textDecoration: "underline",
              font: "inherit",
            }}
            onClick={() => {
              onRefresh();
              setShowAll(false);
            }}
            title="Reload the list"
          >
            Refresh
          </button>
        </div>
      </div>

      {loadingErr && <div className="error">{loadingErr}</div>}
      {inputs === null && !loadingErr && <div className="muted">Loading…</div>}
      {inputs && inputs.length === 0 && (
        <div className="muted small">
          No files have been uploaded for Canvas {env} yet. Drop one above to
          start.
        </div>
      )}

      {inputs && inputs.length > 0 && (
        <>
          <input
            type="text"
            className="filter-search"
            placeholder="Filter by file name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setShowAll(false);
            }}
            style={{ width: "100%", marginBottom: 10 }}
          />

          {filtered.length === 0 ? (
            <div className="muted small">
              No files match <code>{search}</code>.
            </div>
          ) : (
            <>
              <ul className="inputs-list">
                {visible.map((input) => {
                  const dupCount = input.eTag
                    ? (etagCounts.get(input.eTag) ?? 1)
                    : 1;
                  return (
                    <li key={input.key} className="inputs-item">
                      <div>
                        <div className="inputs-item-name">
                          {input.fileName}
                          {dupCount > 1 && (
                            <span
                              className="trigger-pill"
                              style={{ marginLeft: 8 }}
                              title={`The same content has been uploaded ${dupCount} times — these duplicates are visible only when "Show all" is on`}
                            >
                              ×{dupCount}
                            </span>
                          )}
                        </div>
                        <div className="inputs-item-meta muted">
                          {formatBytes(input.size)} · uploaded{" "}
                          <span title={formatDateTime(input.lastModified)}>
                            {formatRelative(input.lastModified)}
                          </span>
                        </div>
                      </div>
                      <div className="muted small inputs-item-meta">
                        {formatDateTime(input.lastModified)}
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => onStartFromExisting(input)}
                        disabled={busy}
                        title="Start a new sync against this file (no re-upload needed)"
                      >
                        Start sync
                      </button>
                    </li>
                  );
                })}
              </ul>

              {(hidden > 0 || showAll) && (
                <div
                  style={{
                    marginTop: 10,
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                  }}
                >
                  {hidden > 0 ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setShowAll(true)}
                    >
                      Show {hidden} more file{hidden === 1 ? "" : "s"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setShowAll(false)}
                    >
                      Show fewer
                    </button>
                  )}
                  <span className="muted small">
                    Showing {visible.length} of {filtered.length}
                    {search.trim() && ` matching "${search.trim()}"`}
                  </span>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
